import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { ConnectionService } from '@services/connection-service';
import { EventLoggerService } from '@services/event-logger-service';
import { TimeService } from '@services/time-service';
import { UserService } from '@services/user';
import { AppConfig } from '../../app.config';
import { AllocatedRegimen } from '../../app-constant-types';
import { DIET_BANNER_PAGE, DIET_BANNER_CONCERN_TYPE, DietBannerPage } from '../../app.constants';

/**
 * ret_homepage_revamp: collapsible "Diet & Lifestyle Guidance" banner shared by the home page and the
 * regimen instruction page. Acquisition (unpaid) users only see it on the instruction page, where it is
 * non-clickable and renders without the chevron. Retention (paid) users see it on both pages — expanded
 * for delivery day 0-2 and collapsed from day 3 onwards on home — and tapping it opens the diet plan page.
 */
@Component({
  selector: 'app-diet-guidance-banner',
  templateUrl: './diet-guidance-banner.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(click)': 'openDietPlan()',
  },
})
export class DietGuidanceBannerComponent {
  private readonly conn = inject(ConnectionService);
  private readonly appConfig = inject(AppConfig);
  private readonly eventLoggerService = inject(EventLoggerService);
  private readonly timeService = inject(TimeService);
  private readonly userService = inject(UserService);

  regimen = input.required<AllocatedRegimen>();
  // 'home' renders the salad-icon header, 'instruction' renders the doctor-avatar header
  page = input<DietBannerPage>(DIET_BANNER_PAGE.INSTRUCTION);

  readonly isInstructionPage = computed((): boolean => this.page() === DIET_BANNER_PAGE.INSTRUCTION);
  readonly isUnpaidUser = computed((): boolean => {
    const user = this.conn.getActingUser();
    return !user || !this.userService.isPaidUser(user);
  });
  readonly isHomePage = computed((): boolean => this.page() === DIET_BANNER_PAGE.HOME);
  readonly isHair = computed((): boolean => this.regimen()?.class === this.appConfig.Shared.Regimen.Class.HAIR);
  readonly isAcneConcern = computed((): boolean => {
    const treatmentConcerns: string[] = this.regimen()?.displayConcern?.treatmentConcerns ?? [];
    return treatmentConcerns.includes(this.appConfig.Shared.String.ACNE_OR_PIMPLES);
  });
  readonly collapsed = computed((): boolean => {
    // Instruction page always shows the expanded banner.
    if (this.isInstructionPage()) return false;
    const user = this.conn.getActingUser();
    if (!user || !this.userService.isPaidUser(user)) return true;
    const deliveredDate = this.regimen()?.deliveredDate;
    if (!deliveredDate) return true;
    return this.timeService.differenceInDays(new Date(), new Date(deliveredDate)) > 2;
  });

  openDietPlan(): void {
    if (this.isUnpaidUser()) return;
    const concernType = this.isHair()
      ? DIET_BANNER_CONCERN_TYPE.HAIR
      : (this.isAcneConcern() ? DIET_BANNER_CONCERN_TYPE.ACNE : DIET_BANNER_CONCERN_TYPE.NON_ACNE);
    this.eventLoggerService.cleverTapEvent('click', JSON.stringify({ name: 'diet_banner_new', concern_type: concernType, page: this.page() }));
    this.conn.navigateToURL('/notice');
  }
}
